import { loadData, isAvailableComponent } from './yinn-wear-data.3fb8ca28.js';
import { createStore } from './yinn-wear-store.2f347b8c.js';
import { restoreDesignFromCart } from './yinn-wear-design-restore.037603e0.js';
import { Notice } from './yinn-wear-notice.e25c6897.js';
import { config, partner } from './yinn-wear-config.7c3b828d.js';
import { h } from './yinn-wear-ui.f3cef344.js';
import { t } from './yinn-wear-i18n.787431a0.js';
import { ProductTypeSwitch } from './yinn-wear-product-type-switch.8e5cfe01.js';
import { BeadPicker } from './yinn-wear-bead-picker.7dc26029.js';
import { BraceletRenderer } from './yinn-wear-bracelet-renderer.bf82dcaa.js';
import { PlaqueRenderer } from './yinn-wear-plaque-renderer.1c2dfb98.js';
import { PlaquePicker } from './yinn-wear-plaque-picker.254c5504.js';
import { PlaqueFooter } from './yinn-wear-plaque-footer.cb3b93d9.js';
import { PlaqueViewToggle } from './yinn-wear-plaque-view.c65a58c4.js';
import { PlaqueReviewModal } from './yinn-wear-plaque-review-modal.b207d9b0.js';
import { PlaqueFullView } from './yinn-wear-plaque-full-view.d2d1a286.js';
import { CustomFormulaModal } from './yinn-wear-custom-formula-modal.ac05ddae.js';
import { BuilderTools } from './yinn-wear-builder-tools.4aea10de.js';
import { BottomControls } from './yinn-wear-bottom-controls.88e21225.js';
import { ReviewModal } from './yinn-wear-review-modal.45657338.js';
import { SaveSheet } from './yinn-wear-save-sheet.593cfe94.js';
import { ImageLightbox } from './yinn-wear-image-lightbox.a98d0ba7.js';
import { CareGuide } from './yinn-wear-care-guide.f20d793e.js';

const $ = (sel) => document.querySelector(sel);

/** Fill the viewport below whatever the theme puts above the section; footer scrolls in after.
 *  Desktop only — on mobile the page scrolls and the app takes its natural height. */
function fitViewport() {
  const app = $('.yw-app');
  if (!app) return;
  if (!window.matchMedia('(min-width: 901px)').matches) {
    app.style.height = '';
    return;
  }
  const top = Math.max(0, Math.round(app.getBoundingClientRect().top + window.scrollY));
  app.style.height = `calc(100vh - ${top}px)`;
}

async function main() {
  const data = await loadData();

  const mark = partner?.mark || null;
  const store = createStore(data.plaque);

  // Every component owns a DOM node from the section. Partner / preview themes
  // ship a smaller shell, so a component is only built when its node exists —
  // otherwise it would blow up on `null.addEventListener`.
  const components = [];
  const add = (el, factory) => {
    if (el) components.push(factory());
  };

  const saveSheetEl = $('[data-save-sheet]');
  const saveSheet = saveSheetEl ? SaveSheet({ el: saveSheetEl }) : null;

  const noticeEl = $('.yw-app');
  const notice = noticeEl ? Notice({ el: noticeEl }) : null;

  const lightboxEl = $('[data-image-lightbox]');
  const lightbox = lightboxEl ? ImageLightbox({ data, el: lightboxEl }) : null;

  // "Care & use" guide — a quiet builder pill that opens a floating panel.
  // Skipped on the white-label partner page, whose copy must not carry YINN.
  if (!partner && $('.yw-builder')) {
    CareGuide({ appEl: $('.yw-app'), builderEl: $('.yw-builder') });
  }

  // Custom formula request dialog (shared by bracelet + plaque). Built first so
  // both review modals can open it. The endpoint is a Cloudflare Worker that
  // opens a Shopify draft order; when unset the dialog reports "could not submit".
  const customEl = $('[data-custom-formula-modal]');
  const customModal = customEl
    ? CustomFormulaModal({ data, el: customEl, endpoint: config.customFormulaEndpoint })
    : null;
  if (customModal) components.push(customModal);
  const openCustom = () => customModal && customModal.show(store.get());

  const modalEl = $('[data-review-modal]');
  const modal = modalEl
    ? ReviewModal({ data, el: modalEl, saveSheet, onCustomFormula: openCustom })
    : null;

  add($('[data-product-switch]'), () => ProductTypeSwitch({ store, el: $('[data-product-switch]') }));
  add($('[data-builder-tools]'), () => BuilderTools({ store, el: $('[data-builder-tools]') }));
  add($('[data-bracelet-stage]'), () =>
    BraceletRenderer({ data, store, el: $('[data-bracelet-stage]'), mark })
  );
  add($('.yw-picker'), () =>
    BeadPicker({
      data,
      store,
      el: $('.yw-picker'),
      onZoom: (mold, formulaId) => lightbox && lightbox.show(mold, formulaId),
      onNotice: (msg) => notice && notice.show(msg),
    })
  );
  add($('[data-bottom-controls]'), () =>
    BottomControls({
      data,
      store,
      el: $('[data-bottom-controls]'),
      onReview: () => modal && modal.show(store.get()),
    })
  );
  if (modal) components.push(modal);

  // Plaque Necklace (shares the shell: Formula, pricing, cart, order data).
  if (data.plaque) {
    const plaqueStage = $('[data-plaque-stage]');
    const plaqueFooterEl = $('[data-plaque-footer]');

    const plaqueModalEl = $('[data-plaque-review-modal]');
    const plaqueModal = plaqueModalEl
      ? PlaqueReviewModal({ data, el: plaqueModalEl, saveSheet, onCustomFormula: openCustom })
      : null;

    add(plaqueStage, () => PlaqueRenderer({ data, el: plaqueStage, mark }));
    add($('[data-plaque-view]'), () => PlaqueViewToggle({ el: $('[data-plaque-view]'), store }));

    // Click the piece (or the zoom button) to see the complete necklace: the
    // builder deliberately crops the back-neck part of the cord.
    const fullViewEl = $('[data-plaque-fullview]');
    if (plaqueStage && fullViewEl) {
      const fullView = PlaqueFullView({ data, el: fullViewEl, mark, store });
      components.push(fullView);
      const openFull = () => fullView.show(store.get());
      plaqueStage.classList.add('is-zoomable');
      plaqueStage.setAttribute('title', t('viewFull'));
      plaqueStage.addEventListener('click', openFull);
      const builderEl = $('.yw-builder');
      if (builderEl) {
        const zoomBtn = h('button', {
          type: 'button',
          class: 'yw-zoom',
          'aria-label': t('viewFull'),
          title: t('viewFull'),
          html:
            '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.5">' +
            '<circle cx="11" cy="11" r="6.5"/><path d="M11 8.4v5.2M8.4 11h5.2M20 20l-3.7-3.7" stroke-linecap="round"/></svg>',
        });
        zoomBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          openFull();
        });
        builderEl.append(zoomBtn);
      }
    }

    if (plaqueFooterEl && plaqueModal) {
      components.push(
        PlaqueFooter({ data, el: plaqueFooterEl, store, onReview: () => plaqueModal.show(store.get()) })
      );
    }
    if (plaqueModal) components.push(plaqueModal);
    add($('.yw-picker'), () =>
      PlaquePicker({ data, store, el: $('.yw-picker'), onCustomFormula: openCustom })
    );
  }

  // Preview mode: ?previewBracelet=<ids>&wrist=<mm>&cord=<id> re-opens an exact
  // bracelet (linked from a custom-formula draft order, which has no static
  // image because a bracelet is an arbitrary bead combination).
  const qp = new URLSearchParams(window.location.search);
  const previewSeq = qp.get('previewBracelet');
  if (previewSeq) {
    const sequence = previewSeq.split(',').filter((id) => data.componentById[id]);
    if (sequence.length) {
      store.hydrateDesign({
        design_id: null,
        product_type: 'bracelet',
        sequence,
        wrist_size_mm: Number(qp.get('wrist')) || undefined,
        cord_component_id: qp.get('cord') || undefined,
      });
    }
  }

  // Edit mode: /pages/yinn-wear?design=BR-XXXXXX restores a design already in the cart.
  const designId = new URLSearchParams(window.location.search).get('design');
  if (designId) {
    try {
      const restored = await restoreDesignFromCart(designId);
      if (restored) {
        // A bead may have been discontinued since this design was saved. Drop it
        // — otherwise the design could never be saved again — and say so.
        const kept = restored.sequence.filter((id) => isAvailableComponent(id, data));
        const dropped = restored.sequence.length - kept.length;
        store.hydrateDesign({ ...restored, sequence: kept });
        if (dropped && notice) notice.show(t('beadsRemoved').replace('{n}', dropped));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  const appEl = $('.yw-app');
  store.subscribe((state) => {
    // Drives which sub-tree of the shell is visible (builder, pickers, footer).
    if (appEl) appEl.classList.toggle('is-plaque', state.productType === 'plaque');
    for (const c of components) c.render(state);
  });

  fitViewport();
  window.addEventListener('resize', fitViewport);
}

main().catch((err) => {
  console.error(err);
  document.body.innerHTML = `<pre style="padding:24px;color:#a33;font:13px/1.6 monospace">${err.message}</pre>`;
});
